import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, MapPin, Phone, X } from 'lucide-react';

import Reveal from '@/components/Reveal';
import type { MapEstablishment, MapPoint } from '@/lib/publicApi';
import { loadEstablishments } from '@/lib/establishments';
import { pluralRu } from '@/utils/format';
import markerHatUrl from '@/images/marker-hat-white.png';
import '@/styles/map.css';

// Ключ из кабинета Яндекса выдан на JS API 2.1: эндпоинт /v3/ отвечает на него
// «Invalid api key» при любом Referer, а /2.1/ отдаёт скрипт. Официальных типов
// для подключения через script-tag нет, поэтому глобал, any.
declare global {
  interface Window {
    ymaps?: any;
  }
}

const YANDEX_JS_API_KEY = (import.meta.env.VITE_YANDEX_MAPS_API_KEY as string | undefined)?.trim();

// 2.1 работает в порядке [широта, долгота].
const RUSSIA_CENTER: [number, number] = [61, 90];
const RUSSIA_ZOOM = 3;

const PIN_PATH = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z';

// Ширина боковой панели на десктопе (Tailwind sm:w-[400px]) + зазоры: на эту
// область карта не должна укладывать точки при зуме на город.
const PANEL_WIDTH = 400;
const PANEL_GUTTER = 24;

// Предел высоты шторки на телефоне: половина карты остаётся видимой. Это же
// число, запасное значение, пока настоящая высота шторки не измерена (первый
// кадр открытия).
const MOBILE_SHEET_MAX = 320;
// Небольшой перелёт за предел дотягиваем целиком: строка заведения, около 85px,
// и список из трёх упирается в предел, не добирая считанных пикселей. Обрезка на
// пять пикселей читается как дефект вёрстки, а не как «ниже есть прокрутка».
const MOBILE_SHEET_SNAP = 40;

// Сводная метка города распадается на метки заведений, когда город занимает
// такую долю экрана. Считаем долю, а не порог зума: у города из двух кофеен на
// одной улице и у города, растянутого на 40 км, «достаточный» зум разный, а
// доля экрана, одна и та же мера. Пока город мельче, заведения наложились бы
// друг на друга, и одна метка со счётчиком читается лучше.
const EXPLODE_RATIO = 0.3;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

// Загружаем скрипт JS API ровно один раз на страницу (даже если компонент
// перемонтируется, например, из-за Fast Refresh в dev).
let ymapsScriptPromise: Promise<void> | null = null;
function loadYmaps(apiKey: string): Promise<void> {
  if (window.ymaps) return Promise.resolve();
  if (!ymapsScriptPromise) {
    ymapsScriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://api-maps.yandex.ru/2.1/?apikey=${encodeURIComponent(apiKey)}&lang=ru_RU`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Не удалось загрузить Яндекс.Карты'));
      document.head.appendChild(script);
    }).catch((e: unknown) => {
      ymapsScriptPromise = null; // даём шанс на повторную попытку при следующем монтировании
      throw e;
    });
  }
  return ymapsScriptPromise;
}

function pinSize(count: number): { w: number; h: number } {
  const scale = Math.min(1.5, 1 + Math.sqrt(Math.max(0, count - 1)) * 0.16);
  return { w: Math.round(32 * scale), h: Math.round(41 * scale) };
}

const EST_PIN = { w: 26, h: 33 };

// Капля-пин с логотипом Resstro + подсказка при наведении/тапе.
// transform: translate(-50%,-100%), острый кончик капли встаёт точно на
// координату: макет вставляется в точку без собственного anchor.
// Размеры обёртки заданы явно: JS API кладёт макет в контейнер 0×0, и без них
// у пина нулевая ширина (ломает hit-testing hover'а и e2e-проверки).
function pinHtml(
  id: string,
  size: { w: number; h: number },
  title: string,
  subtitle: string,
  badge?: number,
  extraClass = '',
): string {
  const { w, h } = size;
  return `
    <div class="map-pin-wrap ${extraClass}" id="${id}" style="width:${w}px;height:${h}px;transform:translate(-50%,-100%)">
      <div class="map-pin" style="width:${w}px;height:${h}px">
        <svg class="map-pin__shape" viewBox="0 0 24 24" preserveAspectRatio="none" aria-hidden="true">
          <path d="${PIN_PATH}"></path>
        </svg>
        <img class="map-pin__icon" src="${markerHatUrl}" alt="" />
        ${badge && badge > 1 ? `<span class="map-pin__count">${badge}</span>` : ''}
      </div>
      <div class="map-pin__tooltip">
        <strong>${escapeHtml(title)}</strong>
        <span>${escapeHtml(subtitle)}</span>
      </div>
    </div>
  `;
}

function extentOf(coords: Array<{ lat: number; lon: number }>) {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  for (const c of coords) {
    if (c.lat < minLat) minLat = c.lat;
    if (c.lat > maxLat) maxLat = c.lat;
    if (c.lon < minLon) minLon = c.lon;
    if (c.lon > maxLon) maxLon = c.lon;
  }
  return { minLat, maxLat, minLon, maxLon, spanLat: maxLat - minLat, spanLon: maxLon - minLon };
}

/**
 * Границы вокруг набора координат. minPad не даёт setBounds уйти в максимальный
 * зум на вырожденном прямоугольнике, когда точка одна или все заведения стоят
 * на одной улице.
 */
function boundsOf(
  coords: Array<{ lat: number; lon: number }>,
  minPadLat: number,
  minPadLon: number,
): [[number, number], [number, number]] {
  const { minLat, maxLat, minLon, maxLon, spanLat, spanLon } = extentOf(coords);
  const latPad = Math.max(spanLat * 0.12, minPadLat);
  const lonPad = Math.max(spanLon * 0.12, minPadLon);
  // bounds у 2.1, [[юг, запад], [север, восток]].
  return [
    [minLat - latPad, minLon - lonPad],
    [maxLat + latPad, maxLon + lonPad],
  ];
}

/**
 * Ставит точку в видимую часть карты: панель закрывает левую четверть на
 * десктопе и hiddenBottom пикселей снизу на телефоне, поэтому центр смещаем на
 * половину скрытой области. targetZoom, если нужно попутно приблизить.
 */
function centerAside(
  map: any,
  lat: number,
  lon: number,
  hiddenBottom: number,
  targetZoom?: number,
) {
  const bounds = map.getBounds?.();
  if (!bounds) return;
  const [[south, west], [north, east]] = bounds as [[number, number], [number, number]];
  const zoom = map.getZoom();
  const target = targetZoom ?? zoom;
  // Каждый шаг зума вдвое уменьшает видимый охват, иначе смещение считалось бы
  // по старому масштабу и уехало бы в разы.
  const scale = 2 ** (target - zoom);
  const viewLat = (north - south) / scale;
  const viewLon = (east - west) / scale;
  const wide = window.matchMedia('(min-width: 640px)').matches;
  const containerH = map.container?.getSize?.()[1] || 1;
  const center: [number, number] = wide
    ? [lat, lon - viewLon * 0.14]
    : [lat - viewLat * (hiddenBottom / containerH) * 0.5, lon];
  map.setCenter(center, target, { duration: 300, checkZoomRange: true });
}

/** Пора ли показывать заведения города по отдельности при текущем масштабе. */
function shouldExplode(map: any, point: MapPoint): boolean {
  // Одно заведение и так стоит на своём адресе, распадаться нечему.
  if (point.establishments.length < 2) return false;
  const bounds = map.getBounds?.();
  if (!bounds) return false;
  const [[south, west], [north, east]] = bounds as [[number, number], [number, number]];
  const viewLat = north - south;
  const viewLon = east - west;
  if (!(viewLat > 0) || !(viewLon > 0)) return false;
  const { spanLat, spanLon } = extentOf(point.establishments);
  return Math.max(spanLat / viewLat, spanLon / viewLon) >= EXPLODE_RATIO;
}

export default function EstablishmentsMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const pinClickRef = useRef(false);
  const [points, setPoints] = useState<MapPoint[] | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // Свёрнутая шторка, только заголовок города: список уезжает вниз, карта под
  // ним открывается целиком, а контекст «какой город смотрим» остаётся.
  const [sheetCollapsed, setSheetCollapsed] = useState(false);
  // Замеры шторки: full, вся карточка, header, её заголовок. Из них считается,
  // на сколько поднять копирайт Яндекса, насколько увести вниз свёрнутый список
  // и куда карте укладывать метки, чтобы они не ушли под шторку.
  const [sheet, setSheet] = useState({ full: MOBILE_SHEET_MAX, header: 70 });
  const handleSheetMeasure = useCallback((m: { full: number; header: number }) => {
    setSheet((prev) => (prev.full === m.full && prev.header === m.header ? prev : m));
  }, []);
  // Видимая высота шторки на телефоне, на неё карта не должна укладывать метки.
  const sheetVisible = sheetCollapsed ? sheet.header : sheet.full;
  // Список городов, показанных по отдельным заведениям,, строкой, чтобы
  // сравнение по значению гасило лишние перерисовки: boundschange у 2.1 летит
  // десятками кадров за один жест, а состав «рассыпанных» городов меняется
  // раз в несколько шагов зума.
  const [explodedKey, setExplodedKey] = useState('');
  const [mapState, setMapState] = useState<'loading' | 'ready' | 'error'>(
    YANDEX_JS_API_KEY ? 'loading' : 'error',
  );

  // Точки и подсветка нужны обработчикам карты, которые ставятся один раз при
  // инициализации: через ref, чтобы не переподписывать события на каждый рендер.
  const pointsRef = useRef<MapPoint[] | null>(null);
  pointsRef.current = points;
  const hoveredIdRef = useRef<string | null>(null);
  hoveredIdRef.current = hoveredId;
  // Обзорное кадрирование делаем один раз: дальше карта принадлежит
  // пользователю, и возвращать её «на место» при закрытии панели, грубо.
  const didFitRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    // Общий кэшированный запрос с hero-плашкой и маркизой заведений, один
    // вызов API на страницу, а не три.
    loadEstablishments()
      .then((pts) => {
        if (!cancelled) setPoints(pts);
      })
      .catch(() => {
        if (!cancelled) setPoints([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Инициализация карты, один раз. Точки добавляются отдельным эффектом,
  // когда готовы и карта, и данные (порядок готовности не гарантирован).
  useEffect(() => {
    if (!YANDEX_JS_API_KEY || !containerRef.current) return;
    let cancelled = false;

    loadYmaps(YANDEX_JS_API_KEY)
      .then(() => new Promise<void>((resolve) => window.ymaps!.ready(resolve)))
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const ymaps = window.ymaps!;

        const map = new ymaps.Map(
          containerRef.current,
          { center: RUSSIA_CENTER, zoom: RUSSIA_ZOOM, controls: [] },
          { suppressMapOpenBlock: true, yandexMapDisablePoiInteractivity: true },
        );
        // На телефоне низ карты занимает шторка со списком, и кнопки зума под ней
        // просто не видны, уводим их наверх, к правому краю.
        const narrow = !window.matchMedia('(min-width: 640px)').matches;
        map.controls.add(
          new ymaps.control.ZoomControl({
            options: {
              size: 'small',
              position: narrow ? { right: 12, top: 64 } : { right: 20, bottom: 40 },
            },
          }),
        );

        // Колёсико по умолчанию выключено, иначе карта перехватывает скролл
        // страницы, когда пользователь просто пролистывает лендинг. Включаем
        // после явного клика по карте, жест становится осознанным.
        map.behaviors.disable('scrollZoom');
        map.events.add('click', () => {
          map.behaviors.enable('scrollZoom');
          // Клик по пину всплывает до карты: не гасим подсказку и не закрываем
          // панель, которую этот же клик только что открыл (важно для тача, где
          // нет mouseleave).
          if (pinClickRef.current) {
            pinClickRef.current = false;
            return;
          }
          document.querySelectorAll('.map-pin-wrap.is-open').forEach((n) => n.classList.remove('is-open'));
        });

        // Масштаб решает, показывать город одной меткой или всеми заведениями:
        // пользователь, который просто приблизил карту, ждёт, что точки
        // разъедутся по своим адресам, а не останутся кучей до клика.
        map.events.add('boundschange', () => {
          const pts = pointsRef.current;
          if (!pts) return;
          setExplodedKey(
            pts
              .filter((p) => shouldExplode(map, p))
              .map((p) => p.city)
              .join('|'),
          );
        });

        mapRef.current = map;
        setMapState('ready');
      })
      .catch(() => {
        if (!cancelled) setMapState('error');
      });

    return () => {
      cancelled = true;
      mapRef.current?.destroy?.();
      mapRef.current = null;
    };
  }, []);

  // Маркеры: пересобираем, когда готова карта, пришли точки или сменился набор
  // «рассыпанных» городов. Что показывать, сводную метку города или каждое
  // заведение, решает масштаб, а не выбранный город: клик по метке всё равно
  // приближает карту, так что оба пути ведут к одной картинке, зато отдалившись
  // пользователь снова видит компактную метку со счётчиком вместо кучи пинов.
  useEffect(() => {
    const map = mapRef.current;
    if (mapState !== 'ready' || !map || !points) return;

    const ymaps = window.ymaps!;
    map.geoObjects.removeAll();

    // Подсказку открываем/закрываем по событиям самого JS API, а не по CSS-hover:
    // поверх макетов маркеров лежит events-pane, который забирает все нативные
    // pointer-события, так что :hover на .map-pin-wrap никогда не срабатывает.
    const closeAll = () =>
      document.querySelectorAll('.map-pin-wrap.is-open').forEach((n) => n.classList.remove('is-open'));

    const addPin = (
      coords: [number, number],
      id: string,
      size: { w: number; h: number },
      html: string,
      onClick: () => void,
    ) => {
      const placemark = new ymaps.Placemark(
        coords,
        {},
        {
          iconLayout: ymaps.templateLayoutFactory.createClass(html),
          iconShape: {
            type: 'Rectangle',
            coordinates: [[-size.w / 2, -size.h], [size.w / 2, 0]],
          },
        },
      );
      // Ищем узел по id при каждом событии: JS API пересобирает макет при
      // перерисовке, поэтому запоминать элемент нельзя.
      const open = () => {
        closeAll();
        document.getElementById(id)?.classList.add('is-open');
      };
      placemark.events.add('mouseenter', open);
      placemark.events.add('mouseleave', closeAll);
      placemark.events.add('click', () => {
        pinClickRef.current = true; // чтобы клик по карте тут же не закрыл подсказку
        open();
        onClick();
      });
      map.geoObjects.add(placemark);
      return placemark;
    };

    for (const [i, p] of points.entries()) {
      if (shouldExplode(map, p)) {
        for (const est of p.establishments) {
          addPin(
            [est.lat, est.lon],
            `map-est-${est.id}`,
            EST_PIN,
            pinHtml(
              `map-est-${est.id}`,
              EST_PIN,
              est.name,
              est.address ?? p.city,
              undefined,
              'map-pin-wrap--sm',
            ),
            () => {
              // Клик по отдельному заведению тоже открывает список города -
              // иначе, приблизив карту, до панели вообще не добраться.
              setSelectedCity(p.city);
              setHoveredId(est.id);
              setSheetCollapsed(false);
            },
          );
        }
      } else {
        const size = pinSize(p.count);
        addPin(
          [p.lat, p.lon],
          `map-pin-${i}`,
          size,
          pinHtml(
            `map-pin-${i}`,
            size,
            p.city,
            `${p.count} ${pluralRu(p.count, 'заведение', 'заведения', 'заведений')} на Resstro`,
            p.count,
          ),
          () => {
            setSelectedCity(p.city);
            setHoveredId(null);
            setSheetCollapsed(false);
          },
        );
      }
    }

    // Подсветку метки, наведённой в панели, восстанавливаем после пересборки:
    // JS API создал узлы заново, класс с прежних не переехал.
    const active = hoveredIdRef.current;
    if (active) document.getElementById(`map-est-${active}`)?.classList.add('is-active');
  }, [mapState, points, explodedKey]);

  // Кадрирование, отдельным эффектом от маркеров: иначе каждый шаг зума
  // пересобирал бы метки и тут же перебивал жест пользователя новым setBounds.
  // Панель перекрывает часть карты, поэтому её область исключаем из зоны
  // укладки точек через zoomMargin [верх, право, низ, лево].
  useEffect(() => {
    const map = mapRef.current;
    if (mapState !== 'ready' || !map || !points || points.length === 0) return;

    const wide = window.matchMedia('(min-width: 640px)').matches;
    const hiddenBottom = sheetVisible;

    // Обзор, только на первом показе. Пока город один, рамка вокруг его центра
    // это экран пустых полей на зуме ~9, из которого до масштаба, где заведения
    // разъезжаются по адресам, четыре щипка; кадрируем сам город по его
    // заведениям, тогда достаточно одного. Городов станет больше, вернётся
    // общая рамка, где 0.12°/0.18° спасают от вырожденных bounds.
    if (!didFitRef.current) {
      didFitRef.current = true;
      map.setBounds(
        points.length === 1 && points[0].establishments.length > 1
          ? boundsOf(points[0].establishments, 0.01, 0.014)
          : boundsOf(points, 0.12, 0.18),
        {
          checkZoomRange: true,
          zoomMargin: wide ? [80, 40, 80, 40] : [40, 24, 40, 24],
          duration: 400,
        },
      );
      return;
    }

    const selected = points.find((p) => p.city === selectedCity);
    // Панель закрыли, карту не трогаем: пользователь сам выбрал, куда смотреть,
    // и откат к обзору выглядел бы как потеря места.
    if (!selected || selected.establishments.length === 0) return;

    // Город уже показан по заведениям, значит масштаб и так подходящий. Не
    // перекадрируем (на телефоне это отбрасывало обратно к сводной метке: под
    // шторкой остаётся полоса, в которую весь город влезает только зумом ниже
    // порога «рассыпания»), а лишь выводим выбранную точку из-под панели.
    if (shouldExplode(map, selected)) {
      const focused = selected.establishments.find((e) => e.id === hoveredIdRef.current);
      const [lat, lon] = focused
        ? [focused.lat, focused.lon]
        : (map.getCenter() as [number, number]);
      centerAside(map, lat, lon, hiddenBottom);
      return;
    }

    // ~0.004° по широте ≈ 450 м: заведения одного города часто стоят на одной
    // улице, и без этого запаса карта прыгает в предельный зум.
    map.setBounds(boundsOf(selected.establishments, 0.004, 0.006), {
      checkZoomRange: true,
      zoomMargin: wide
        ? [48, 48, 48, PANEL_WIDTH + PANEL_GUTTER * 2]
        : [16, 16, hiddenBottom + 8, 16],
      duration: 400,
    });
    // sheetVisible намеренно не в зависимостях: он меняется при сворачивании
    // шторки, и карта дёргалась бы новым setBounds на каждое нажатие «свернуть».
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapState, points, selectedCity]);

  // Подсветка метки при наведении на карточку в панели, без пересборки
  // маркеров: трогаем класс уже отрисованного узла.
  useEffect(() => {
    document.querySelectorAll('.map-pin-wrap.is-active').forEach((n) => n.classList.remove('is-active'));
    if (!hoveredId) return;
    const node = document.getElementById(`map-est-${hoveredId}`);
    node?.classList.add('is-active');
  }, [hoveredId]);

  // Esc закрывает панель, ожидаемо для оверлея.
  useEffect(() => {
    if (!selectedCity) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedCity(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedCity]);

  // Тап по карточке в списке: на телефоне hover'а нет, и панель была мёртвой -
  // теперь карта подъезжает к заведению и подсвечивает его метку.
  const focusEstablishment = (est: MapEstablishment) => {
    setHoveredId(est.id);
    const map = mapRef.current;
    if (!map) return;
    // Не ниже 15: на обзорном масштабе заведения ещё не разъехались по адресам,
    // и подсвечивать было бы нечего.
    centerAside(map, est.lat, est.lon, sheetVisible, Math.max(map.getZoom(), 15));
  };

  const totalCount = useMemo(() => (points ?? []).reduce((sum, p) => sum + p.count, 0), [points]);
  const cityCount = points?.length ?? 0;
  const statsReady = points !== null && totalCount > 0;

  const selectedPoint = points?.find((p) => p.city === selectedCity) ?? null;
  // Панель не размонтируется, иначе не сыграет анимация закрытия: пока она
  // уезжает, показываем последний открытый город.
  const lastPointRef = useRef<MapPoint | null>(null);
  if (selectedPoint) lastPointRef.current = selectedPoint;
  const panelPoint = selectedPoint ?? lastPointRef.current;

  return (
    <section id="map" className="section-divider scroll-mt-24 bg-white pt-20 sm:pt-28 lg:pt-36">
      <div className="container-site pb-14 sm:pb-16">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-6">
            <Reveal>
              <h2 className="h2">Каждая точка на&nbsp;карте: работающее заведение</h2>
            </Reveal>
          </div>
          <Reveal delay={80} className="md:col-span-5 md:col-start-8 md:pt-10">
            <p className="lead">
              Не макет и не стоковые цифры: координаты подтягиваются из реальных
              подключений и обновляются, когда на Resstro открывается новое заведение.
            </p>
          </Reveal>
        </div>
      </div>

      <Reveal delay={140} className="relative">
        {/* 600px на телефоне вместо 480: половину карты забирает шторка со
            списком, и на 480 сверху оставался тонкий поясок, в который город
            влезал только на масштабе ниже порога «рассыпания» точек. */}
        {/* --sheet-lift, на сколько map.css поднимает обязательную строку
            копирайта Яндекса: шторка прижата к низу карты и иначе накрыла бы её.
            --sheet-slide, на сколько уезжает вниз свёрнутый список.
            --sheet-max, предел роста карточки; берём из того же замера, что и
            подъём копирайта, иначе они разъедутся на «дотянутых» списках. */}
        <div
          className="map-frame relative h-[600px] w-full overflow-hidden lg:h-[720px] bg-[#eeece6]"
          style={
            {
              '--sheet-lift': `${selectedPoint ? sheetVisible : 0}px`,
              '--sheet-slide': `${sheetCollapsed ? sheet.full - sheet.header : 0}px`,
              '--sheet-max': `${sheet.full}px`,
            } as React.CSSProperties
          }
        >
          <div ref={containerRef} className="h-full w-full" />

          {mapState === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center">
              <p className="text-[15px] font-medium text-ink/60">Карта временно недоступна</p>
              <p className="max-w-xs text-[13px] text-ink/40">
                Попробуйте обновить страницу чуть позже
              </p>
            </div>
          )}

          <CityPanel
            point={panelPoint}
            open={!!selectedPoint}
            collapsed={sheetCollapsed}
            hoveredId={hoveredId}
            onHover={setHoveredId}
            onSelect={focusEstablishment}
            onToggleCollapsed={() => setSheetCollapsed((v) => !v)}
            onMeasure={handleSheetMeasure}
            // Свёрнутость не сбрасываем: иначе при закрытии карточка успевала бы
            // раскрыться на те же 300 мс, что уезжает вниз. Сброс делает клик по
            // метке, новый город всегда открывается со списком.
            onClose={() => {
              setSelectedCity(null);
              setHoveredId(null);
            }}
          />

          {/* bottom-9 на узких экранах: ниже идёт обязательная строка копирайта
              Яндекса, перекрывать её нельзя. Открытая панель на мобильном стоит
              ровно на месте плашки, там её прячем; на широком экране просто
              отводим правее панели (400px карточки + отступы). */}
          {statsReady && mapState === 'ready' && (
            <div
              className={`pointer-events-none absolute inset-x-4 bottom-9 z-[5100] flex justify-start transition-all duration-300 ease-out sm:inset-x-8 sm:bottom-8 ${
                selectedPoint
                  ? 'opacity-0 sm:translate-x-[368px] sm:opacity-100'
                  : 'opacity-100 sm:translate-x-0'
              }`}
            >
              <div className="pointer-events-auto rounded-2xl border border-line/70 bg-white/90 px-5 py-4 shadow-lift backdrop-blur-md">
                <LiveStats totalCount={totalCount} cityCount={cityCount} />
              </div>
            </div>
          )}

          {/* Подсказка «нажмите на метку», только пока ничего не выбрано. */}
          {statsReady && mapState === 'ready' && !selectedPoint && (
            <div className="pointer-events-none absolute inset-x-0 top-5 z-[5100] flex justify-center px-4">
              <span className="rounded-full border border-line/70 bg-white/85 px-4 py-2 text-[13px] text-ink/55 shadow-soft backdrop-blur-md">
                Нажмите на метку, чтобы увидеть заведения
              </span>
            </div>
          )}
        </div>
      </Reveal>
    </section>
  );
}

// ─── Боковая панель с заведениями города ──────────────────────────────────
// На широких экранах, колонка слева, на узких, шторка снизу: панель шириной
// 400px на телефоне заняла бы весь экран и спрятала карту, к которой она
// относится.
//
// z-[6000], не «на всякий случай»: JS API держит свои слои на z-index до 5002
// (events-pane 2500, копирайты 5002), и events-pane прозрачным полотном
// перехватывает все pointer-события. С меньшим z-index панель видна, но
// полностью мертва: не работают ни кнопка закрытия, ни телефон, ни скролл.
function CityPanel({
  point,
  open,
  collapsed,
  hoveredId,
  onHover,
  onSelect,
  onToggleCollapsed,
  onClose,
  onMeasure,
}: {
  point: MapPoint | null;
  open: boolean;
  collapsed: boolean;
  hoveredId: string | null;
  onHover: (id: string | null) => void;
  onSelect: (est: MapEstablishment) => void;
  onToggleCollapsed: () => void;
  onClose: () => void;
  onMeasure: (m: { full: number; header: number }) => void;
}) {
  const headerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Размеры шторки отдаём наружу: по ним карта поднимает строку копирайта
  // Яндекса, уводит вниз свёрнутый список и исключает занятую полосу из зоны
  // укладки меток. Высоту списка берём натуральную (scrollHeight): фактическая
  // обрезана max-height, а нужна та, к которой карточка стремится.
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const measure = () => {
      const natural = header.offsetHeight + (listRef.current?.scrollHeight ?? 0);
      onMeasure({
        header: header.offsetHeight,
        full: natural <= MOBILE_SHEET_MAX + MOBILE_SHEET_SNAP ? natural : MOBILE_SHEET_MAX,
      });
    };
    measure();
    // Ширина меняет высоту карточек (адрес переносится), пересчитываем.
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [onMeasure, point]);

  if (!point) return null;

  return (
    // Обёртка на телефоне, во всю ширину и вплотную к низу: шторка выезжает
    // снизу экрана, как в нативных картах, поэтому ни отступов, ни верхней
    // границы (высота = высота карточки, translate-y-full убирает её целиком).
    // На широком экране это по-прежнему колонка слева с зазором p-6.
    <div
      className={`pointer-events-none absolute z-[6000] inset-x-0 bottom-0 flex flex-col justify-end transition-transform duration-300 ease-out sm:inset-x-auto sm:inset-y-0 sm:left-0 sm:w-[400px] sm:justify-start sm:p-6 ${
        open
          ? 'translate-y-0 sm:translate-x-0'
          : 'translate-y-full sm:translate-y-0 sm:-translate-x-[110%]'
      }`}
      aria-hidden={!open}
      // Уехавшая панель ещё в разметке (иначе не сыграет анимация закрытия) -
      // без inert её кнопки продолжали бы ловить табуляцию за краем карты.
      inert={!open}
    >
      {/* Предел высоты приходит из замера (--sheet-max), запасное значение, тот
          же MOBILE_SHEET_MAX. Скругление только сверху: карточка прижата к краю
          карты, снизу радиус выглядел бы как оторвавшаяся плашка. На десктопе -
          полноценная карточка (max-h-full, у обёртки задана inset-y-0). */}
      <div
        className={`map-sheet flex max-h-[var(--sheet-max,320px)] flex-col overflow-hidden rounded-t-3xl border border-b-0 border-line/70 bg-white/95 shadow-lift backdrop-blur-xl sm:max-h-full sm:rounded-3xl sm:border-b ${
          open ? 'pointer-events-auto' : ''
        }`}
      >
        <div
          ref={headerRef}
          className="flex items-start justify-between gap-2 border-b border-line/70 px-4 py-3 sm:gap-3 sm:px-5 sm:py-4"
        >
          <div className="min-w-0">
            <h3 className="truncate text-lg font-medium leading-tight text-ink sm:text-xl">
              {point.city}
            </h3>
            <p className="mt-0.5 text-[13px] text-ink/55">
              {point.count} {pluralRu(point.count, 'заведение', 'заведения', 'заведений')} на Resstro
            </p>
          </div>
          <div className="-mr-1 -mt-0.5 flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? 'Развернуть список' : 'Свернуть список'}
              aria-expanded={!collapsed}
              className="rounded-full p-2 text-ink/45 transition-colors hover:bg-ink/5 hover:text-ink"
            >
              {collapsed ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрыть список"
              className="rounded-full p-2 text-ink/45 transition-colors hover:bg-ink/5 hover:text-ink"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Сворачивание: на телефоне карточка целиком уезжает вниз за край карты
            (transform в map.css), поэтому список тут не трогаем, иначе высота
            менялась бы дважды. На десктопе карточка прижата к верху, уезжать ей
            некуда: там сворачиваем сам список, grid-template-rows 1fr→0fr.
            Список при этом inert, иначе он ловил бы табуляцию и читался
            скринридером, оставаясь невидимым. */}
        <div
          className={`grid min-h-0 flex-1 transition-[grid-template-rows] duration-300 ease-out ${
            collapsed ? 'grid-rows-[1fr] sm:grid-rows-[0fr]' : 'grid-rows-[1fr]'
          }`}
          inert={collapsed}
        >
          <div className="min-h-0 overflow-hidden">
            <div ref={listRef} className="h-full overflow-y-auto overscroll-contain">
              {point.establishments.map((est) => (
                <EstablishmentRow
                  key={est.id}
                  est={est}
                  active={hoveredId === est.id}
                  onHover={onHover}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Карточка, не <button>: внутри ссылка «позвонить», а вложенные интерактивные
// элементы невалидны. Отсюда div с role="button" и обработкой клавиатуры.
function EstablishmentRow({
  est,
  active,
  onHover,
  onSelect,
}: {
  est: MapEstablishment;
  active: boolean;
  onHover: (id: string | null) => void;
  onSelect: (est: MapEstablishment) => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onMouseEnter={() => onHover(est.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onSelect(est)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(est);
        }
      }}
      className={`flex cursor-pointer gap-3 border-b border-line/50 px-4 py-3 transition-colors last:border-b-0 sm:gap-3.5 sm:px-5 sm:py-4 ${
        active ? 'bg-orange/5' : 'hover:bg-ink/[0.02]'
      }`}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-line/70 bg-ink/[0.03] sm:h-11 sm:w-11">
        {est.logoUrl ? (
          <img src={est.logoUrl} alt="" className="h-full w-full object-contain" loading="lazy" />
        ) : (
          <span className="text-[15px] font-medium text-ink/40">{est.name.trim().charAt(0)}</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-medium leading-snug text-ink">{est.name}</p>
        {est.address && (
          <p className="mt-0.5 flex items-start gap-1.5 text-[13px] leading-snug text-ink/55 sm:mt-1">
            <MapPin size={13} className="mt-0.5 shrink-0 text-ink/35" />
            <span className="min-w-0">{est.address}</span>
          </p>
        )}
        {est.phone && (
          <a
            href={`tel:${est.phone.replace(/[^\d+]/g, '')}`}
            // Клик по телефону не должен ещё и уводить карту.
            onClick={(e) => e.stopPropagation()}
            className="mt-1 inline-flex items-center gap-1.5 text-[13px] text-ink/70 transition-colors hover:text-orange sm:mt-1.5"
          >
            <Phone size={13} className="shrink-0 text-ink/35" />
            {est.phone}
          </a>
        )}
      </div>
    </div>
  );
}

function LiveStats({ totalCount, cityCount }: { totalCount: number; cityCount: number }) {
  return (
    <div className="flex items-center gap-4 sm:gap-5">
      <div className="whitespace-nowrap">
        <span className="text-lg font-medium tabular-nums text-ink">{totalCount}</span>
        <span className="ml-1.5 text-[13px] text-ink/55">
          {pluralRu(totalCount, 'заведение', 'заведения', 'заведений')}
        </span>
      </div>
      <div className="h-8 w-px bg-line" />
      <div className="whitespace-nowrap">
        <span className="text-lg font-medium tabular-nums text-ink">{cityCount}</span>
        <span className="ml-1.5 text-[13px] text-ink/55">
          {pluralRu(cityCount, 'город', 'города', 'городов')}
        </span>
      </div>
    </div>
  );
}
